import { useContext, useEffect, useState } from "react";
import { UserContext } from "../../../hooks/UserContext";
import { useCompanyContext } from "../../../hooks/CompanyContext";
import { API } from "../../../api";
import type { Branch, Company, Contact, Deal, Grower, Job } from "../../../models";
import { ImportOption, ImportOptionChoices } from "../../../utilities/types";
import { toast } from "react-toastify";
import * as Sentry from "@sentry/react";
import { INVALID_USERCONTEXT } from "../../../utilities/constants";
import { Oops } from "../../../utilities/oops";
import { ImportFormViewModel, JobSubmissionFormProps } from "./ImportForm.types";
import { getImportOptions, sortContacts } from "./ImportForm.helpers";
import type { FeatureStats } from "../ImportTable/ImportTable/ImportTable.types";

// oxlint-disable-next-line no-unused-vars
export function useImportFormViewModel(props: JobSubmissionFormProps): ImportFormViewModel {
	const userContext = useContext(UserContext);
	const {
		selectedCompany: contextSelectedCompany,
		companies: contextCompanyOptions,
		contact,
		isInitialized: companyContextInitialized,
	} = useCompanyContext();
	const [user, setUser] = useState<Contact>();

	const [deals, setDeals] = useState<Deal[]>([]);
	const [growers, setGrowers] = useState<Grower[]>([]);
	const [, setSelectedGrowers] = useState<Grower[]>([]);
	const companyOptions = contextCompanyOptions;
	const [branches, setBranches] = useState<Branch[]>([]);
	const selectedCompany = contextSelectedCompany;
	const [salespersons, setSalespersons] = useState<Contact[]>([]);
	const [loading, setLoading] = useState(false);
	const [importOptions, setImportOptions] = useState<ImportOption[]>([]);
	const [dataImportOption, setDataImportOption] = useState<ImportOption | null>(null);
	const [lastCropOptions, setLastCropOptions] = useState<string[]>([]);
	const [nextCropOptions, setNextCropOptions] = useState<string[]>([]);

	// Modal state for each import type
	const [geoDataModalOpen, setGeoDataModalOpen] = useState(false);
	const [proagricaModalOpen, setProagricaModalOpen] = useState(false);
	const [agrianModalOpen, setAgrianModalOpen] = useState(false);

	// Track whether each modal has been used (opened at least once)
	const [geoDataModalUsed, setGeoDataModalUsed] = useState(false);
	const [proagricaModalUsed, setProagricaModalUsed] = useState(false);
	const [agrianModalUsed, setAgrianModalUsed] = useState(false);

	// Jobs extracted from modal, displayed in ImportTable on main page
	const [extractedJobs, setExtractedJobs] = useState<Job[]>([]);
	const [featureStats, setFeatureStats] = useState<FeatureStats | undefined>(undefined);
	const [activeImportType, setActiveImportType] = useState<ImportOption | null>(null);

	// Key to force remount of form components (for clearing state after submit)
	const [formKey, setFormKey] = useState(0);

	// Handler for when jobs are extracted from a modal form
	const handleJobsExtracted = (jobs: Job[], importType: ImportOption, stats?: FeatureStats) => {
		setExtractedJobs(jobs);
		setFeatureStats(stats);
		setActiveImportType(importType);
		// Close the appropriate modal
		if (importType.id === ImportOptionChoices.BulkUpload2.id) {
			setGeoDataModalOpen(false);
		}
		if (importType.id === ImportOptionChoices.Proagrica.id) setProagricaModalOpen(false);
		if (importType.id === ImportOptionChoices.Agrian.id) setAgrianModalOpen(false);
	};

	// Clear extracted jobs (e.g., when user cancels or starts over)
	const handleClearExtractedJobs = () => {
		setExtractedJobs([]);
		setFeatureStats(undefined);
		setActiveImportType(null);
	};

	// Reset everything after successful job submission
	const handleResetAfterSubmit = () => {
		handleClearExtractedJobs();
		// Clear modal used flags
		setGeoDataModalUsed(false);
		setProagricaModalUsed(false);
		setAgrianModalUsed(false);
		// Increment formKey to force remount and clear form states
		setFormKey((k) => k + 1);
	};

	// Fetch non-company-specific data (import options, crop options)
	useEffect(() => {
		async function getInitialData() {
			try {
				if (userContext == undefined || !userContext.id) {
					Sentry.captureMessage("userContext is undefined: JobSubmissionForm.tsx", "info");
					toast.error(INVALID_USERCONTEXT);
					return;
				}
				const user = contact ?? (await API.Data.Contacts.get.byId(userContext.id));
				setUser(user);
				const importOpts = await getImportOptions(user, contextSelectedCompany, branches);

				setImportOptions(importOpts);
				if (importOpts.length === 1) {
					setDataImportOption(importOpts[0]);
				}
				setLastCropOptions(await API.Data.Jobs.get.lastCropOptions());
				setNextCropOptions(await API.Data.Jobs.get.nextCropOptions());
				setLoading(false);
			} catch (error) {
				Oops.handleError(error, "SubmitJobs.JobSubmissionForm.getData");
			}
		}

		if (!deals.length) {
			setLoading(true);
			getInitialData();
		}
	}, [userContext]);

	// React to company changes from CompanyContext
	useEffect(() => {
		if (!companyContextInitialized || !contextSelectedCompany) {
			return;
		}
		loadCompanyData(contextSelectedCompany);
	}, [contextSelectedCompany?.id, companyContextInitialized]);

	async function loadCompanyData(company: Company | null) {
		setLoading(true);
		if (company) {
			const branches = await API.Data.Branches.get.byCompany(company);
			setBranches(branches);

			const growers = await API.Data.Growers.get.byCompany(company);
			setSelectedGrowers([]);
			setGrowers(growers);

			const deals = await API.Data.Deals.get.byCompany(company);
			setDeals(deals);

			const salespersons = await getSalespersons(company, branches);
			setSalespersons(salespersons);
		}
		setLoading(false);
	}

	async function getSalespersons(company: Company, branches: Branch[]) {
		const contacts = await API.Data.Contacts.get.byCompany(company);
		contacts.sort((a, b) => a.name.localeCompare(b.name));
		return sortContacts(contacts, branches);
	}

	return {
		// State
		userContext,
		user,
		deals,
		growers,
		companyOptions,
		branches,
		selectedCompany,
		salespersons,
		loading,
		importOptions,
		dataImportOption,
		lastCropOptions,
		nextCropOptions,

		// Modal state
		geoDataModalOpen,
		proagricaModalOpen,
		agrianModalOpen,
		geoDataModalUsed,
		proagricaModalUsed,
		agrianModalUsed,
		extractedJobs,
		featureStats,
		activeImportType,
		formKey,

		// Handlers
		onCompanyChanged: loadCompanyData,
		setDataImportOption,
		setLoading,

		// Modal handlers
		setGeoDataModalOpen,
		setProagricaModalOpen,
		setAgrianModalOpen,
		setGeoDataModalUsed,
		setProagricaModalUsed,
		setAgrianModalUsed,
		handleJobsExtracted,
		handleClearExtractedJobs,
		setExtractedJobs,
		handleResetAfterSubmit,
	};
}
